import BetterCotton from '@/assets/images/better_cotton.png';
import { PrintIcon } from '@/assets/PrintIcon';
import { ImprovedCottonEnum } from '@/common/constants/improved.cotton.constants';
import { CottonWithStickinessIcon } from '@/common/icons/CottonWithStickiness';
import { OrganicCottonIcon } from '@/common/icons/OrganicCotton';
import { IUser } from '@/common/models';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
} from '@/components/ui/dialog';
import { formatDate } from '@/lib/format-date';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

interface IPrintDocsProps {
  printDocs: () => void;
  organization: IUser | null;
  contract: any;
  id: string;
  open: boolean;
  setOpen: (open: boolean) => void;
  improved_cotton:
    | ImprovedCottonEnum.BETTER_COTTON
    | ImprovedCottonEnum.COTTON_WITH_STICKINESS
    | ImprovedCottonEnum.ORGANIC
    | null;
  created_at: string;
  load: any;
}

export const PrintDocsModal: FC<IPrintDocsProps> = ({
  printDocs,
  organization,
  id,
  contract,
  open,
  setOpen,
  improved_cotton,
  created_at,
  load,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const getIcon = (
    icon_type:
      | ImprovedCottonEnum.BETTER_COTTON
      | ImprovedCottonEnum.COTTON_WITH_STICKINESS
      | ImprovedCottonEnum.ORGANIC,
  ) => {
    switch (icon_type) {
      case ImprovedCottonEnum.BETTER_COTTON:
        return (
          <img src={BetterCotton} alt="better-cotton" className="w-[150px]" />
        );
      case ImprovedCottonEnum.COTTON_WITH_STICKINESS:
        return <CottonWithStickinessIcon />;
      default:
        return <OrganicCottonIcon />;
    }
  };

  const handleClose = (open: boolean) => {
    navigate('/loads/ALL');
  };

  return (
    <Dialog open={open} onOpenChange={() => handleClose(open)}>
      {/* <DialogTrigger asChild>{children}</DialogTrigger> */}
      {id && (
        <DialogContent className="max-w-[880px] text-black">
          <div className="flex-v-center-center space-y-12 p-4">
            <div className="w-full flex flex-col text-sm">
              <div className="flex justify-between">
                <div className="flex flex-col space-y-1">
                  <p>
                    {t('cotton.cleaning.enterprise')}: {contract?.buyer_name}
                  </p>
                  <p>
                    {t('cotton.preparation.point')}: {organization?.place_name}
                  </p>
                  <p>
                    {t('farmer.name')}: {load?.farmer_name}
                  </p>
                  <p>
                    {t('farmer.inn')}: {load?.tax_number}
                  </p>
                </div>
                <div className="flex flex-col space-y-1">
                  <p className="text-lg font-semibold text-right">2XL</p>
                  <p className="text-right text-2xl">
                    {t('id.number.colon')}{' '}
                    <span className="text-3xl font-bold">{id}</span>
                  </p>
                  <p>
                    {t('created.at')}: {formatDate(created_at)}
                  </p>
                </div>
              </div>
            </div>
            {improved_cotton !== null && (
              <div className="flex h-center space-y-6">
                {getIcon(improved_cotton)}
              </div>
            )}
            <div className="flex-v-center">
              <p className="font-medium">{t('cotton.analysis.results')}</p>
              <DialogTitle className="text-2xl font-semibold">
                {t('information.note.22')}
              </DialogTitle>
            </div>
            <div className="w-full flex-h-center-between">
              <p className="text-lg font-semibold">
                {t('laboratory.conclusion')}
              </p>
              <p className="text-sm font-medium">
                {organization?.district_name}
              </p>
            </div>
            <div className="w-full grid grid-cols-2 gap-x-12 gap-y-4 text-black text-sm">
              <div className="">
                <div className="flex items-end w-[95%]">
                  <span className="font-medium">{t('cotton.pollution')}</span>
                  <div className="flex-grow border-b-2 border-gray-600 mx-4"></div>
                  <span>%</span>
                </div>
                <div className="flex items-end mt-4 mb-6 w-[95%]">
                  <span className="font-medium">{t('cotton.humidity')}</span>
                  <div className="flex-grow border-b-2 border-gray-600 mx-4"></div>
                  <span>%</span>
                </div>
                <div className="flex items-end">
                  <span className=" font-medium">
                    {t('laboratory.manager')}
                  </span>
                  <div className="flex-grow border-b-2 border-gray-600 ml-4"></div>
                </div>
              </div>

              <div>
                <div className="flex items-end">
                  <span className="font-medium mr-2">
                    {t('cotton.variety')}
                  </span>
                  <div className="flex-grow border-b-2 border-gray-600 mx-4"></div>
                  <span className="font-medium mr-2">{t('cotton.class')}</span>
                  <div className="flex-grow border-b-2 border-gray-600 ml-2"></div>
                </div>
                <div className="flex items-end mt-4 mb-6">
                  <span className=" font-medium">{t('cotton.type')}</span>
                  <div className="flex-grow border-b-2 border-gray-600 ml-4"></div>
                </div>
                <div className="flex items-end">
                  <span className=" font-medium">
                    {t('senior.laboratory.assistant')}
                  </span>
                  <div className="flex-grow border-b-2 border-gray-600 ml-4"></div>
                </div>
              </div>
              <div className="flex items-end">
                <span className=" font-medium">{t('farm.representative')}</span>
                <div className="flex-grow border-b-2 border-gray-600 ml-4"></div>
              </div>
            </div>
          </div>

          <DialogFooter className="absolute -bottom-20 left-[50%] translate-x-[-50%]">
            <Button
              variant="outline"
              onClick={printDocs}
              className="font-medium flex-h-center"
            >
              <PrintIcon />
              <span>{t('print')}</span>
            </Button>
          </DialogFooter>
        </DialogContent>
      )}
    </Dialog>
  );
};
